(function () {
  'use strict';

  const STORAGE_KEY = 'peachmode_rush_location';
  const RETURN_STATE_KEY = 'peachmode_rush_return';
  const RETURN_STATE_TTL = 30 * 60 * 1000;
  const PAGE_CACHE_PREFIX = 'peachmode_rush_page:';
  const PAGE_CACHE_INDEX_KEY = 'peachmode_rush_page_cache_index';
  const PAGE_CACHE_TTL = 10 * 60 * 1000;
  const PAGE_CACHE_LIMIT = 12;
  const LOCATION_TTL = 60 * 60 * 1000;
  const LOCATION_REFRESH_DISTANCE_METERS = 100;
  const FACET_CONFIG = [
    { key: 'productTypes', label: 'Category', responseKey: 'productTypes' },
    { key: 'sizes', label: 'Size', responseKey: 'sizes' },
    { key: 'colors', label: 'Color', responseKey: 'colors' }
  ];
  const QUICK_TYPE_CONFIG = [
    { match: 'Saree', label: 'Sarees', icon: 'saree' },
    { match: 'Dress Material', label: 'Dress Materials', icon: 'fabric' },
    { match: 'Salwar Suit', label: 'Salwar Suits', icon: 'salwar' },
    { match: 'Kurti', label: 'Kurtis', icon: 'kurti' },
    { match: 'Top', label: 'Tops', icon: 'top' }
  ];

  class RushCollection extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.endpoint = this.dataset.endpoint;
      this.pageSize = Number(this.dataset.pageSize) || 24;
      this.location = null;
      this.facets = null;
      this.abortController = null;
      this.isLoading = false;
      this.hasMore = false;
      this.hasPrevious = false;
      this.failedLoad = null;
      this.minLoadedPage = null;
      this.maxLoadedPage = null;
      this.totalPages = 1;
      this.loadedPages = new Set();
      this.restoreAnchorHandle = null;
      this.restoreAnchorOffset = null;
      this.restoreScrollY = null;
      this.lastScrollY = window.scrollY;
      this.scrollingUp = false;
      this.scrollFrame = null;
      this.state = {
        page: 1,
        sort: 'newest',
        filters: {
          productTypes: [],
          sizes: [],
          colors: [],
          priceMin: null,
          priceMax: null
        }
      };

      this.restoreStateFromUrl();
      this.cacheElements();
      this.bindEvents();
      this.setupInfiniteScroll();
      this.boundHandleScroll = this.handleScroll.bind(this);
      window.addEventListener('scroll', this.boundHandleScroll, { passive: true });
      this.restoreLocation();
    }

    disconnectedCallback() {
      if (this.loadMoreObserver) this.loadMoreObserver.disconnect();
      if (this.loadPreviousObserver) this.loadPreviousObserver.disconnect();
      if (this.abortController) this.abortController.abort();
      if (this.boundHandleScroll) window.removeEventListener('scroll', this.boundHandleScroll);
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
    }

    cacheElements() {
      this.elements = {
        locationGate: this.querySelector('[data-rush-location-gate]'),
        useLocation: this.querySelector('[data-rush-use-location]'),
        useLocationLabel: this.querySelector('[data-rush-location-button-label]'),
        locationError: this.querySelector('[data-rush-location-error]'),
        testLocation: this.querySelector('[data-rush-test-location]'),
        changeLocation: this.querySelector('[data-rush-change-location]'),
        catalog: this.querySelector('[data-rush-catalog]'),
        typeShortcuts: this.querySelector('[data-rush-type-shortcuts]'),
        typeList: this.querySelector('[data-rush-type-list]'),
        resultCount: this.querySelector('[data-rush-result-count]'),
        grid: this.querySelector('[data-rush-grid]'),
        loading: this.querySelector('[data-rush-loading]'),
        requestError: this.querySelector('[data-rush-request-error]'),
        empty: this.querySelector('[data-rush-empty]'),
        loadMore: this.querySelector('[data-rush-load-more]'),
        loadMoreSpinner: this.querySelector('[data-rush-load-more-spinner]'),
        loadMoreText: this.querySelector('[data-rush-load-more-text]'),
        loadMoreRetry: this.querySelector('[data-rush-load-more-retry]'),
        loadPrevious: this.querySelector('[data-rush-load-previous]'),
        loadPreviousSpinner: this.querySelector('[data-rush-load-previous-spinner]'),
        loadPreviousButton: this.querySelector('[data-rush-load-previous-button]'),
        filters: this.querySelector('[data-rush-filters]'),
        facetList: this.querySelector('[data-rush-facet-list]'),
        activeFilters: this.querySelector('[data-rush-active-filters]'),
        filterCount: this.querySelector('[data-rush-filter-count]'),
        sort: this.querySelector('[data-rush-sort]'),
        sortSheet: this.querySelector('[data-rush-sort-sheet]'),
        sortOptions: Array.from(this.querySelectorAll('[data-rush-sort-option]'))
      };

      this.syncSortControls();
    }

    bindEvents() {
      this.addEventListener('click', (event) => {
        const target = event.target;
        const useLocation = target.closest('[data-rush-use-location]');
        const changeLocation = target.closest('[data-rush-change-location]');
        const testLocation = target.closest('[data-rush-test-location]');
        const openFilters = target.closest('[data-rush-open-filters]');
        const closeFilters = target.closest('[data-rush-close-filters]');
        const openSort = target.closest('[data-rush-open-sort]');
        const closeSort = target.closest('[data-rush-close-sort]');
        const clearFilters = target.closest('[data-rush-clear-filters]');
        const applyFilters = target.closest('[data-rush-apply-filters]');
        const retry = target.closest('[data-rush-retry]');
        const loadMoreRetry = target.closest('[data-rush-load-more-retry]');
        const removeFilter = target.closest('[data-rush-remove-filter]');
        const quickType = target.closest('[data-rush-product-type]');
        const productLink = target.closest('[data-rush-product-link]');

        if (productLink) this.saveListingState(productLink);

        if (useLocation) this.requestLocation();
        if (changeLocation) this.requestLocation();
        if (testLocation) this.useTestLocation();
        if (openFilters) this.toggleFilters(true);
        if (closeFilters) this.toggleFilters(false);
        if (openSort) this.toggleSort(true);
        if (closeSort) this.toggleSort(false);
        if (clearFilters) this.clearFilters();
        if (applyFilters) {
          this.toggleFilters(false);
          this.applyFiltersFromControls();
        }
        if (retry) this.fetchProducts();
        if (loadMoreRetry) {
          const failed = this.failedLoad;
          this.failedLoad = null;
          this.fetchProducts({ placement: failed?.placement || 'append', page: failed?.page || this.maxLoadedPage + 1 });
        }
        if (target.closest('[data-rush-load-previous-button]')) {
          if (this.failedLoad?.placement === 'prepend') this.failedLoad = null;
          this.loadPreviousPage();
        }
        if (removeFilter) this.removeFilter(removeFilter.dataset.rushRemoveFilter, removeFilter.dataset.rushValue);
        if (quickType) this.applyQuickType(quickType.dataset.rushProductType);
      });

      this.elements.sort.addEventListener('change', () => {
        this.applySort(this.elements.sort.value);
      });

      this.elements.sortOptions.forEach((option) => {
        option.addEventListener('change', () => {
          if (option.checked) this.applySort(option.value, true);
        });
      });

      this.elements.facetList.addEventListener('change', (event) => {
        if (window.matchMedia('(min-width: 1000px)').matches) {
          this.applyFiltersFromControls();
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.toggleFilters(false);
          this.toggleSort(false);
        }
      });
    }

    setupInfiniteScroll() {
      this.loadMoreObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
      }, { rootMargin: '500px 0px' });
      this.loadMoreObserver.observe(this.elements.loadMore);

      this.loadPreviousObserver = new IntersectionObserver((entries) => {
        if (this.scrollingUp && entries.some((entry) => entry.isIntersecting)) this.loadPreviousPage();
      }, { rootMargin: '350px 0px' });
      this.loadPreviousObserver.observe(this.elements.loadPrevious);
    }

    restoreStateFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const sort = params.get('rush_sort');
      if (['newest', 'bestselling_30d', 'price_asc', 'price_desc'].includes(sort)) this.state.sort = sort;

      const requestedPage = Number(params.get('rush_page'));
      if (Number.isInteger(requestedPage) && requestedPage > 0) this.state.page = requestedPage;

      const historyState = window.history.state || {};
      const savedState = this.readSavedListingState();
      const restoreState = historyState.rushAnchorHandle
        ? historyState
        : savedState && this.urlsMatch(savedState.url, window.location.href) ? savedState : null;
      if (restoreState) {
        this.restoreAnchorHandle = restoreState.rushAnchorHandle || restoreState.anchorHandle || null;
        const anchorOffset = Number(restoreState.rushAnchorOffset ?? restoreState.anchorOffset);
        this.restoreAnchorOffset = Number.isFinite(anchorOffset) ? anchorOffset : null;
        const scrollY = Number(restoreState.rushScrollY ?? restoreState.scrollY);
        this.restoreScrollY = Number.isFinite(scrollY) && scrollY >= 0 ? scrollY : null;
      }

      FACET_CONFIG.forEach((facet) => {
        const values = params.getAll(`rush_${facet.key}`);
        if (values.length) this.state.filters[facet.key] = values;
      });

      const min = params.has('rush_min') ? Number(params.get('rush_min')) : null;
      const max = params.has('rush_max') ? Number(params.get('rush_max')) : null;
      if (min !== null && Number.isFinite(min) && min >= 0) this.state.filters.priceMin = min;
      if (max !== null && Number.isFinite(max) && max >= 0) this.state.filters.priceMax = max;
    }

    async restoreLocation() {
      let saved = null;

      try {
        saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
        if (saved && Date.now() - saved.savedAt < LOCATION_TTL && Number.isFinite(saved.latitude) && Number.isFinite(saved.longitude)) {
          this.location = saved;
          this.showCatalog();
          this.fetchProducts();
        } else {
          saved = null;
        }
      } catch (error) {
        localStorage.removeItem(STORAGE_KEY);
      }

      if (!saved) this.showLocationGate();
      await this.refreshLocationIfGranted(saved);
    }

    async refreshLocationIfGranted(saved) {
      if (!navigator.geolocation || !navigator.permissions?.query) return;

      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        if (permission.state !== 'granted') return;

        const position = await this.getBrowserLocation({
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
        const refreshed = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracyMeters: Math.round(position.coords.accuracy || 0),
          savedAt: Date.now()
        };

        localStorage.setItem(STORAGE_KEY, JSON.stringify(refreshed));

        if (!saved || this.locationChanged(saved, refreshed)) {
          this.setLocation(refreshed, false);
        } else {
          this.location = refreshed;
        }
      } catch (error) {
        // Cached results remain usable if a silent location refresh fails.
      }
    }

    locationChanged(previous, current) {
      const toRadians = (degrees) => degrees * Math.PI / 180;
      const latitudeDelta = toRadians(current.latitude - previous.latitude);
      const longitudeDelta = toRadians(current.longitude - previous.longitude);
      const previousLatitude = toRadians(previous.latitude);
      const currentLatitude = toRadians(current.latitude);
      const haversine = Math.sin(latitudeDelta / 2) ** 2
        + Math.cos(previousLatitude) * Math.cos(currentLatitude) * Math.sin(longitudeDelta / 2) ** 2;
      const distance = 6371000 * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));

      return distance >= LOCATION_REFRESH_DISTANCE_METERS;
    }

    async requestLocation() {
      this.elements.locationError.hidden = true;
      this.elements.testLocation.hidden = true;
      this.elements.useLocation.disabled = true;
      this.elements.useLocationLabel.textContent = 'Finding your location...';

      if (!navigator.geolocation) {
        this.showLocationError('Location is not supported by this browser.');
        return;
      }

      try {
        let position;
        try {
          position = await this.getBrowserLocation({ enableHighAccuracy: true, timeout: 12000, maximumAge: 300000 });
        } catch (error) {
          if (error.code !== error.TIMEOUT) throw error;
          this.elements.useLocationLabel.textContent = 'Trying another location method...';
          position = await this.getBrowserLocation({ enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 });
        }

        this.setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracyMeters: Math.round(position.coords.accuracy || 0),
          savedAt: Date.now()
        }, true);
      } catch (error) {
        let message = 'We could not access your location. Please try again.';
        if (error.code === error.PERMISSION_DENIED) {
          message = 'Location access is blocked. Allow it in your browser settings, then try again.';
        } else if (error.code === error.TIMEOUT) {
          message = this.isLocalPreview()
            ? 'This preview browser could not provide a location. You can use the test location below.'
            : 'Finding your location took too long. Please try again.';
        }
        this.showLocationError(message);
      }
    }

    getBrowserLocation(options) {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, options);
      });
    }

    setLocation(location, persist) {
      this.location = location;
      if (persist) localStorage.setItem(STORAGE_KEY, JSON.stringify(this.location));
      this.state.page = 1;
      this.showCatalog();
      this.fetchProducts();
    }

    useTestLocation() {
      this.setLocation({
        latitude: 13.063632,
        longitude: 77.587582,
        accuracyMeters: 25
      }, false);
    }

    isLocalPreview() {
      return ['127.0.0.1', 'localhost'].includes(window.location.hostname);
    }

    showLocationError(message) {
      this.elements.useLocation.disabled = false;
      this.elements.useLocationLabel.textContent = 'Find nearby styles';
      this.elements.locationError.textContent = message;
      this.elements.locationError.hidden = false;
      this.elements.testLocation.hidden = !this.isLocalPreview();
    }

    showLocationGate() {
      this.elements.catalog.hidden = true;
      this.elements.locationGate.hidden = false;
      this.elements.changeLocation.hidden = true;
      this.elements.useLocation.disabled = false;
      this.elements.useLocationLabel.textContent = 'Find nearby styles';
      this.elements.testLocation.hidden = !this.isLocalPreview();
    }

    showCatalog() {
      this.elements.locationGate.hidden = true;
      this.elements.catalog.hidden = false;
      this.elements.changeLocation.hidden = false;
    }

    async fetchProducts({ placement = 'replace', page = this.state.page } = {}) {
      if (!this.location) return;
      if (placement !== 'replace' && (this.isLoading || this.loadedPages.has(page))) return;
      if (placement === 'append' && !this.hasMore) return;
      if (placement === 'prepend' && !this.hasPrevious) return;
      if (placement === 'replace' && this.abortController) this.abortController.abort();

      const filters = {};
      Object.entries(this.state.filters).forEach(([key, value]) => {
        if (Array.isArray(value) ? value.length : value !== null) filters[key] = value;
      });

      const payload = {
        latitude: this.location.latitude,
        longitude: this.location.longitude,
        accuracyMeters: this.location.accuracyMeters,
        page,
        pageSize: this.pageSize,
        sort: this.state.sort,
        filters
      };

      this.failedLoad = null;
      this.isLoading = true;
      this.setLoading(true, placement);
      const cached = this.readPageCache(payload);
      if (cached) {
        this.renderResponse(cached, { placement, page });
        this.isLoading = false;
        this.setLoading(false, placement);
        return;
      }

      const controller = new AbortController();
      this.abortController = controller;
      let failed = false;

      try {
        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
          signal: controller.signal,
          credentials: 'same-origin'
        });

        if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
        const data = await response.json();
        this.writePageCache(payload, data);
        this.renderResponse(data, { placement, page });
      } catch (error) {
        if (error.name !== 'AbortError') {
          failed = true;
          this.showRequestError(placement, page);
        }
      } finally {
        if (this.abortController === controller) {
          this.isLoading = false;
          if (!failed) this.setLoading(false, placement);
        }
      }
    }

    renderResponse(data, { placement, page }) {
      this.elements.requestError.hidden = true;
      this.facets = data.facets || this.facets;
      if (this.facets) {
        this.renderFacets();
        this.renderQuickTypes();
      }

      const products = Array.isArray(data.products) ? data.products : [];
      const pagination = data.pagination || { page: 1, totalPages: 1, total: products.length };
      const currentPage = Number(pagination.page) || page;
      const totalPages = Number(pagination.totalPages) || currentPage;

      const serviceable = data.serviceArea ? data.serviceArea.serviceable : data.serviceable;
      if (!serviceable || !products.length) {
        if (placement === 'replace') {
          this.elements.grid.replaceChildren();
          this.elements.empty.hidden = false;
          this.elements.empty.style.display = 'flex';
        }
      } else {
        this.elements.empty.hidden = true;
        this.elements.empty.style.display = '';
        this.renderProducts(products, placement, currentPage);
      }

      this.totalPages = totalPages;
      if (placement === 'replace') {
        this.loadedPages = new Set([currentPage]);
        this.minLoadedPage = currentPage;
        this.maxLoadedPage = currentPage;
        this.state.page = currentPage;
      } else if (products.length) {
        this.loadedPages.add(currentPage);
        this.minLoadedPage = Math.min(this.minLoadedPage, currentPage);
        this.maxLoadedPage = Math.max(this.maxLoadedPage, currentPage);
      }
      this.hasMore = Boolean(serviceable && products.length && this.maxLoadedPage < totalPages);
      this.hasPrevious = Boolean(serviceable && products.length && this.minLoadedPage > 1);
      this.failedLoad = null;
      this.updatePagingControls();

      const total = Number(pagination.total ?? pagination.totalItems) || 0;
      this.elements.resultCount.textContent = `${this.formatNumber(total)} ${total === 1 ? 'style' : 'styles'} available for quick delivery`;
      this.renderActiveFilters();
      if (placement === 'replace') {
        this.updateUrl(currentPage);
        this.restoreSavedPosition();
      }
    }

    renderFacets() {
      const fragment = document.createDocumentFragment();

      FACET_CONFIG.forEach((config, index) => {
        let values = Array.isArray(this.facets[config.responseKey]) ? [...this.facets[config.responseKey]] : [];
        if (config.key === 'sizes') values = this.sortSizeValues(values);
        if (!values.length) return;

        const details = document.createElement('details');
        details.className = 'rush__facet';
        details.open = index < 2;
        const summary = document.createElement('summary');
        summary.textContent = config.label;
        details.append(summary);

        const options = document.createElement('div');
        options.className = 'rush__facet-options';
        values.forEach((item) => {
          const value = typeof item === 'string' ? item : item.value;
          const count = typeof item === 'string' ? null : item.count;
          if (!value) return;

          const label = document.createElement('label');
          label.className = 'rush__facet-option';
          const input = document.createElement('input');
          input.type = 'checkbox';
          input.name = config.key;
          input.value = value;
          input.checked = this.state.filters[config.key].includes(value);
          const text = document.createElement('span');
          text.textContent = value;
          label.append(input, text);
          if (count !== null && count !== undefined) {
            const countElement = document.createElement('span');
            countElement.className = 'rush__facet-count';
            countElement.textContent = this.formatNumber(count);
            label.append(countElement);
          }
          options.append(label);
        });
        details.append(options);
        fragment.append(details);
      });

      if (this.facets.priceRange) {
        const details = document.createElement('details');
        details.className = 'rush__facet';
        const summary = document.createElement('summary');
        summary.textContent = 'Price';
        details.append(summary);
        const inputs = document.createElement('div');
        inputs.className = 'rush__price-inputs';
        inputs.append(
          this.createPriceInput('Minimum', 'priceMin', this.facets.priceRange.min),
          this.createPriceInput('Maximum', 'priceMax', this.facets.priceRange.max)
        );
        details.append(inputs);
        fragment.append(details);
      }

      this.elements.facetList.replaceChildren(fragment);
    }

    renderQuickTypes() {
      const productTypes = Array.isArray(this.facets.productTypes) ? this.facets.productTypes : [];
      const shortcuts = QUICK_TYPE_CONFIG.map((config) => {
        const facet = productTypes.find((item) => String(item.value || item).toLowerCase() === config.match.toLowerCase());
        return facet ? { ...config, value: facet.value || facet, count: facet.count, image: facet.primaryImage || null } : null;
      }).filter(Boolean);

      if (!shortcuts.length) {
        this.elements.typeShortcuts.hidden = true;
        return;
      }

      const fragment = document.createDocumentFragment();
      fragment.append(this.createQuickTypeButton('', 'All styles', 'all', null, null));
      shortcuts.forEach((shortcut) => {
        fragment.append(this.createQuickTypeButton(shortcut.value, shortcut.label, shortcut.icon, shortcut.count, shortcut.image));
      });

      this.elements.typeList.replaceChildren(fragment);
      this.elements.typeShortcuts.hidden = false;
    }

    createQuickTypeButton(value, label, icon, count, imageUrl) {
      const activeTypes = this.state.filters.productTypes;
      const isActive = value === '' ? activeTypes.length === 0 : activeTypes.includes(value);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'rush__type-button';
      button.dataset.rushProductType = value;
      button.setAttribute('aria-pressed', String(isActive));
      button.setAttribute('aria-label', count ? `${label}, ${this.formatNumber(count)} styles` : label);

      const graphic = document.createElement('span');
      graphic.className = 'rush__type-graphic';
      if (imageUrl) {
        const image = document.createElement('img');
        image.className = 'rush__type-image';
        image.src = imageUrl;
        image.alt = '';
        image.loading = 'lazy';
        image.width = 132;
        image.height = 132;
        image.addEventListener('error', () => {
          image.replaceWith(this.createTypeIcon(icon));
        }, { once: true });
        graphic.append(image);
      } else {
        graphic.append(this.createTypeIcon(icon));
      }
      const text = document.createElement('span');
      text.className = 'rush__type-label';
      text.textContent = label;
      button.append(graphic, text);
      return button;
    }

    createTypeIcon(icon) {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 48 48');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke', 'currentColor');
      svg.setAttribute('stroke-width', '1.7');
      svg.setAttribute('stroke-linecap', 'round');
      svg.setAttribute('stroke-linejoin', 'round');

      const paths = {
        all: ['M13 13h9v9h-9z', 'M26 13h9v9h-9z', 'M13 26h9v9h-9z', 'M26 26h9v9h-9z'],
        saree: ['M18 8h12l4 31H14l4-31z', 'M18 9c8 6 11 14 15 23', 'M17 21h14', 'M16 30h17'],
        fabric: ['M11 15h25v8H11z', 'M14 23h25v8H14z', 'M10 31h25v7H10z', 'M31 17l3 3', 'M34 25l3 3'],
        salwar: ['M18 9l-8 6 4 7 4-3v18h12V19l4 3 4-7-8-6', 'M21 9c0 3 6 3 6 0', 'M18 27h12', 'M20 37l-2 7', 'M28 37l2 7'],
        kurti: ['M18 9l-8 6 4 7 4-3v20h12V19l4 3 4-7-8-6', 'M21 9c0 3 6 3 6 0', 'M18 29h12'],
        top: ['M17 10l-9 6 5 8 5-3v17h12V21l5 3 5-8-9-6', 'M20 10c0 4 8 4 8 0']
      };

      (paths[icon] || paths.all).forEach((pathData) => {
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', pathData);
        svg.append(path);
      });
      return svg;
    }

    applyQuickType(value) {
      this.state.filters.productTypes = value ? [value] : [];
      this.state.page = 1;
      this.renderFacets();
      this.renderQuickTypes();
      this.renderActiveFilters();
      this.updateUrl();
      this.fetchProducts();
    }

    createPriceInput(labelText, name, placeholder) {
      const label = document.createElement('label');
      label.textContent = labelText;
      const input = document.createElement('input');
      input.type = 'number';
      input.inputMode = 'numeric';
      input.min = '0';
      input.name = name;
      input.placeholder = this.formatNumber(placeholder || 0);
      input.value = this.state.filters[name] ?? '';
      label.append(input);
      return label;
    }

    applyFiltersFromControls() {
      FACET_CONFIG.forEach((config) => {
        this.state.filters[config.key] = Array.from(
          this.elements.facetList.querySelectorAll(`input[name="${config.key}"]:checked`),
          (input) => input.value
        );
      });

      ['priceMin', 'priceMax'].forEach((key) => {
        const input = this.elements.facetList.querySelector(`input[name="${key}"]`);
        const value = input && input.value !== '' ? Number(input.value) : null;
        this.state.filters[key] = Number.isFinite(value) ? value : null;
      });

      this.state.page = 1;
      this.updateUrl();
      this.fetchProducts();
    }

    clearFilters() {
      FACET_CONFIG.forEach((config) => { this.state.filters[config.key] = []; });
      this.state.filters.priceMin = null;
      this.state.filters.priceMax = null;
      this.state.page = 1;
      this.toggleFilters(false);
      this.renderFacets();
      this.renderQuickTypes();
      this.renderActiveFilters();
      this.updateUrl();
      this.fetchProducts();
    }

    removeFilter(key, value) {
      if (Array.isArray(this.state.filters[key])) {
        this.state.filters[key] = this.state.filters[key].filter((item) => item !== value);
      } else if (key in this.state.filters) {
        this.state.filters[key] = null;
      }
      this.state.page = 1;
      this.renderFacets();
      this.renderQuickTypes();
      this.renderActiveFilters();
      this.updateUrl();
      this.fetchProducts();
    }

    renderActiveFilters() {
      const fragment = document.createDocumentFragment();
      let count = 0;

      FACET_CONFIG.forEach((config) => {
        this.state.filters[config.key].forEach((value) => {
          fragment.append(this.createFilterChip(config.key, value, value));
          count += 1;
        });
      });

      if (this.state.filters.priceMin !== null) {
        fragment.append(this.createFilterChip('priceMin', String(this.state.filters.priceMin), `From ${this.formatMoney(this.state.filters.priceMin)}`));
        count += 1;
      }
      if (this.state.filters.priceMax !== null) {
        fragment.append(this.createFilterChip('priceMax', String(this.state.filters.priceMax), `Up to ${this.formatMoney(this.state.filters.priceMax)}`));
        count += 1;
      }

      this.elements.activeFilters.replaceChildren(fragment);
      this.elements.activeFilters.hidden = count === 0;
      this.elements.activeFilters.style.display = count ? 'flex' : '';
      this.elements.filterCount.textContent = count;
      this.elements.filterCount.hidden = count === 0;
    }

    createFilterChip(key, value, label) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'rush__active-filter';
      button.dataset.rushRemoveFilter = key;
      button.dataset.rushValue = value;
      button.setAttribute('aria-label', `Remove ${label} filter`);
      const text = document.createElement('span');
      text.textContent = label;
      const close = document.createElement('span');
      close.textContent = '×';
      close.setAttribute('aria-hidden', 'true');
      button.append(text, close);
      return button;
    }

    renderProducts(products, placement = 'replace', page = this.state.page) {
      const fragment = document.createDocumentFragment();
      products.forEach((product) => fragment.append(this.createProductCard(product, page)));
      if (placement === 'append') {
        this.elements.grid.append(fragment);
      } else if (placement === 'prepend') {
        const previousHeight = document.documentElement.scrollHeight;
        const previousScrollY = window.scrollY;
        this.elements.grid.prepend(fragment);
        window.requestAnimationFrame(() => {
          const heightDelta = document.documentElement.scrollHeight - previousHeight;
          window.scrollTo({ top: previousScrollY + heightDelta, behavior: 'auto' });
        });
      } else {
        this.elements.grid.replaceChildren(fragment);
      }
    }

    createProductCard(product, page) {
      const article = document.createElement('article');
      article.className = 'rush-card';
      const productPath = product.handle ? `/products/${encodeURIComponent(product.handle)}` : product.productUrl;
      article.dataset.rushPage = String(page);
      article.dataset.rushHandle = product.handle || this.productHandleFromUrl(productPath);

      const imageLink = document.createElement('a');
      imageLink.className = 'rush-card__image-link';
      imageLink.href = productPath;
      imageLink.dataset.rushProductLink = '';
      imageLink.setAttribute('aria-label', product.title || 'View product');

      const badge = document.createElement('span');
      badge.className = 'rush-card__badge';
      const deliveryGroups = this.getDeliveryGroups(product.eligibleVariants || []);
      badge.textContent = deliveryGroups.hasSameDay && !deliveryGroups.hasNextDay
        ? 'Same day'
        : deliveryGroups.hasNextDay && !deliveryGroups.hasSameDay
          ? 'Next day'
          : 'Rush';
      imageLink.append(badge);

      const price = Number(product.price) || 0;
      const compareAtPrice = Number(product.compareAtPrice) || 0;
      if (compareAtPrice > price) {
        const discount = document.createElement('span');
        discount.className = 'rush-card__discount';
        discount.textContent = `${Math.round((1 - price / compareAtPrice) * 100)}% off`;
        imageLink.append(discount);
      }

      if (product.primaryImage) {
        const image = document.createElement('img');
        image.className = 'rush-card__image';
        image.src = product.primaryImage;
        image.alt = product.title || '';
        image.loading = 'lazy';
        image.width = 540;
        image.height = 720;
        imageLink.append(image);
      }

      const info = document.createElement('div');
      info.className = 'rush-card__info';
      const titleLink = document.createElement('a');
      titleLink.href = productPath;
      titleLink.className = 'rush-card__title';
      titleLink.dataset.rushProductLink = '';
      titleLink.textContent = product.title || 'Untitled product';

      const priceRow = document.createElement('div');
      priceRow.className = 'rush-card__price-row';
      const priceElement = document.createElement('span');
      priceElement.className = 'rush-card__price';
      priceElement.textContent = this.formatMoney(price);
      priceRow.append(priceElement);
      if (compareAtPrice > price) {
        const compareElement = document.createElement('span');
        compareElement.className = 'rush-card__compare-price';
        compareElement.textContent = this.formatMoney(compareAtPrice);
        priceRow.append(compareElement);
      }

      const sizesElement = document.createElement('div');
      sizesElement.className = 'rush-card__sizes';
      const sizes = this.sortSizeValues(
        Array.from(new Set((product.eligibleVariants || []).map((variant) => variant.size).filter(Boolean)))
      );
      if (sizes.length) {
        const sizeLabel = document.createElement('span');
        sizeLabel.className = 'rush-card__sizes-label';
        sizeLabel.textContent = 'Available';
        sizesElement.append(sizeLabel);

        sizes.slice(0, 4).forEach((size) => {
          const sizeChip = document.createElement('span');
          sizeChip.className = 'rush-card__size';
          sizeChip.textContent = this.formatSize(size);
          sizesElement.append(sizeChip);
        });

        if (sizes.length > 4) {
          const moreSizes = document.createElement('span');
          moreSizes.className = 'rush-card__size rush-card__size--more';
          moreSizes.textContent = `+${sizes.length - 4}`;
          sizesElement.append(moreSizes);
        }
      }

      info.append(titleLink, priceRow, sizesElement);
      article.append(imageLink, info);
      return article;
    }

    getDeliveryGroups(variants) {
      const bySize = new Map();
      let hasSameDay = false;
      let hasNextDay = false;

      variants.forEach((variant) => {
        if (!variant) return;
        const tags = Array.isArray(variant.deliveryTags)
          ? variant.deliveryTags.map((tag) => String(tag).toUpperCase())
          : [];
        const tier = tags.includes('SAME_DAY') ? 'sameDay' : tags.includes('NEXT_DAY') ? 'nextDay' : null;
        if (!tier) return;
        if (tier === 'sameDay') hasSameDay = true;
        if (tier === 'nextDay') hasNextDay = true;
        if (!variant.size) return;

        const key = String(variant.size).trim().toUpperCase();
        const current = bySize.get(key);
        if (!current || tier === 'sameDay') bySize.set(key, { value: variant.size, tier });
      });

      const grouped = { sameDay: [], nextDay: [], hasSameDay, hasNextDay };
      bySize.forEach((entry) => grouped[entry.tier].push(entry.value));
      grouped.sameDay = this.sortSizeValues(grouped.sameDay);
      grouped.nextDay = this.sortSizeValues(grouped.nextDay);
      return grouped;
    }

    applySort(value, closeSheet = false) {
      if (!['newest', 'bestselling_30d', 'price_asc', 'price_desc'].includes(value)) return;
      this.state.sort = value;
      this.state.page = 1;
      this.syncSortControls();
      if (closeSheet) this.toggleSort(false);
      this.updateUrl();
      this.fetchProducts();
    }

    syncSortControls() {
      if (this.elements.sort) this.elements.sort.value = this.state.sort;
      this.elements.sortOptions.forEach((option) => {
        option.checked = option.value === this.state.sort;
      });
    }

    sortSizeValues(values) {
      const order = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL', '4XL', '5XL', '6XL', 'FREE SIZE', 'ONE SIZE', 'UNSTITCHED'];
      const rank = (item) => {
        const value = typeof item === 'string' ? item : item.value;
        const normalized = String(value || '').trim().toUpperCase();
        const index = order.indexOf(normalized);
        return index === -1 ? order.length : index;
      };

      return values.sort((left, right) => {
        const rankDifference = rank(left) - rank(right);
        if (rankDifference !== 0) return rankDifference;
        const leftValue = typeof left === 'string' ? left : left.value;
        const rightValue = typeof right === 'string' ? right : right.value;
        return String(leftValue).localeCompare(String(rightValue), undefined, { numeric: true });
      });
    }

    formatSize(size) {
      const normalized = String(size).trim();
      return normalized.toUpperCase() === 'UNSTITCHED' ? 'Unstitched' : normalized.toUpperCase();
    }

    loadNextPage() {
      if (this.isLoading || !this.hasMore || this.failedLoad) return;
      this.fetchProducts({ placement: 'append', page: this.maxLoadedPage + 1 });
    }

    loadPreviousPage() {
      if (this.isLoading || !this.hasPrevious || this.failedLoad) return;
      this.fetchProducts({ placement: 'prepend', page: this.minLoadedPage - 1 });
    }

    updatePagingControls() {
      this.elements.loadMore.hidden = !this.hasMore;
      this.elements.loadMore.style.display = this.hasMore ? 'flex' : '';
      this.elements.loadMoreSpinner.hidden = true;
      this.elements.loadMoreText.textContent = 'Loading more styles...';
      this.elements.loadMoreText.hidden = true;
      this.elements.loadMoreRetry.hidden = true;

      this.elements.loadPrevious.hidden = !this.hasPrevious;
      this.elements.loadPrevious.style.display = this.hasPrevious ? 'flex' : '';
      this.elements.loadPreviousSpinner.hidden = true;
      this.elements.loadPreviousButton.hidden = false;
      this.elements.loadPreviousButton.textContent = 'Load previous styles';
    }

    updateUrl(page = this.state.page) {
      const url = new URL(window.location.href);
      Array.from(url.searchParams.keys()).forEach((key) => {
        if (key.startsWith('rush_')) url.searchParams.delete(key);
      });

      if (this.state.sort !== 'newest') url.searchParams.set('rush_sort', this.state.sort);
      FACET_CONFIG.forEach((config) => {
        this.state.filters[config.key].forEach((value) => url.searchParams.append(`rush_${config.key}`, value));
      });
      if (this.state.filters.priceMin !== null) url.searchParams.set('rush_min', String(this.state.filters.priceMin));
      if (this.state.filters.priceMax !== null) url.searchParams.set('rush_max', String(this.state.filters.priceMax));
      if (Number(page) > 1) url.searchParams.set('rush_page', String(page));
      window.history.replaceState({ ...(window.history.state || {}) }, '', url);
      return url.href;
    }

    saveListingState(productLink) {
      const card = productLink.closest('.rush-card');
      const page = Number(card?.dataset.rushPage) || this.state.page;
      const anchorHandle = card?.dataset.rushHandle || this.productHandleFromUrl(productLink.href);
      const anchorOffset = card ? Math.round(card.getBoundingClientRect().top) : null;
      this.state.page = page;
      const url = this.updateUrl(page);
      const state = {
        url,
        scrollY: Math.max(0, Math.round(window.scrollY)),
        anchorHandle,
        anchorOffset,
        page,
        productPath: new URL(productLink.href, window.location.origin).pathname,
        savedAt: Date.now()
      };

      window.history.replaceState({
        ...(window.history.state || {}),
        rushScrollY: state.scrollY,
        rushAnchorHandle: state.anchorHandle,
        rushAnchorOffset: state.anchorOffset
      }, '', url);

      try {
        sessionStorage.setItem(RETURN_STATE_KEY, JSON.stringify(state));
      } catch (error) {
        // Browser history still restores the listing if session storage is unavailable.
      }
    }

    readSavedListingState() {
      try {
        const state = JSON.parse(sessionStorage.getItem(RETURN_STATE_KEY));
        if (!state || Date.now() - Number(state.savedAt) > RETURN_STATE_TTL) return null;
        return state;
      } catch (error) {
        return null;
      }
    }

    urlsMatch(left, right) {
      try {
        const leftUrl = new URL(left, window.location.origin);
        const rightUrl = new URL(right, window.location.origin);
        return leftUrl.pathname === rightUrl.pathname && leftUrl.search === rightUrl.search;
      } catch (error) {
        return false;
      }
    }

    productHandleFromUrl(value) {
      try {
        const parts = new URL(value, window.location.origin).pathname.split('/').filter(Boolean);
        const productIndex = parts.indexOf('products');
        return productIndex >= 0 ? decodeURIComponent(parts[productIndex + 1] || '') : '';
      } catch (error) {
        return '';
      }
    }

    handleScroll() {
      const currentScrollY = window.scrollY;
      this.scrollingUp = currentScrollY < this.lastScrollY;
      this.lastScrollY = currentScrollY;
      if (this.scrollFrame) return;

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        const cards = Array.from(this.elements.grid.querySelectorAll('.rush-card'));
        if (!cards.length) return;

        const targetY = window.innerHeight * 0.35;
        let closest = null;
        let closestDistance = Infinity;
        cards.forEach((card) => {
          const rect = card.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top > window.innerHeight) return;
          const distance = Math.abs(rect.top - targetY);
          if (distance < closestDistance) {
            closest = card;
            closestDistance = distance;
          }
        });

        const visiblePage = Number(closest?.dataset.rushPage);
        if (Number.isInteger(visiblePage) && visiblePage > 0 && visiblePage !== this.state.page) {
          this.state.page = visiblePage;
          this.updateUrl(visiblePage);
        }
      });
    }

    pageCacheKey(payload) {
      const cacheShape = {
        latitude: Number(payload.latitude).toFixed(4),
        longitude: Number(payload.longitude).toFixed(4),
        page: payload.page,
        pageSize: payload.pageSize,
        sort: payload.sort,
        filters: payload.filters
      };
      return `${PAGE_CACHE_PREFIX}${JSON.stringify(cacheShape)}`;
    }

    readPageCache(payload) {
      const key = this.pageCacheKey(payload);
      try {
        const cached = JSON.parse(sessionStorage.getItem(key));
        if (!cached || Date.now() - Number(cached.savedAt) > PAGE_CACHE_TTL) {
          sessionStorage.removeItem(key);
          this.removePageCacheIndexEntry(key);
          return null;
        }
        this.touchPageCacheIndex(key);
        return cached.data;
      } catch (error) {
        return null;
      }
    }

    writePageCache(payload, data) {
      const key = this.pageCacheKey(payload);
      try {
        sessionStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), data }));
        this.touchPageCacheIndex(key);
      } catch (error) {
        this.prunePageCache([]);
      }
    }

    touchPageCacheIndex(key) {
      let keys = [];
      try {
        keys = JSON.parse(sessionStorage.getItem(PAGE_CACHE_INDEX_KEY)) || [];
      } catch (error) {
        keys = [];
      }
      keys = keys.filter((item) => item !== key);
      keys.push(key);
      this.prunePageCache(keys);
    }

    removePageCacheIndexEntry(key) {
      try {
        const keys = (JSON.parse(sessionStorage.getItem(PAGE_CACHE_INDEX_KEY)) || []).filter((item) => item !== key);
        sessionStorage.setItem(PAGE_CACHE_INDEX_KEY, JSON.stringify(keys));
      } catch (error) {
        // Cache indexing is optional; product loading does not depend on it.
      }
    }

    prunePageCache(keys) {
      try {
        const activeKeys = Array.isArray(keys)
          ? keys
          : JSON.parse(sessionStorage.getItem(PAGE_CACHE_INDEX_KEY)) || [];
        while (activeKeys.length > PAGE_CACHE_LIMIT) {
          sessionStorage.removeItem(activeKeys.shift());
        }
        sessionStorage.setItem(PAGE_CACHE_INDEX_KEY, JSON.stringify(activeKeys));
      } catch (error) {
        // Ignore storage quota and privacy-mode failures; network loading remains available.
      }
    }

    restoreSavedPosition() {
      const anchorHandle = this.restoreAnchorHandle;
      const anchorOffset = this.restoreAnchorOffset;
      const scrollY = this.restoreScrollY;
      this.restoreAnchorHandle = null;
      this.restoreAnchorOffset = null;
      this.restoreScrollY = null;

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          const card = anchorHandle
            ? Array.from(this.elements.grid.querySelectorAll('.rush-card')).find((item) => item.dataset.rushHandle === anchorHandle)
            : null;
          if (card && anchorOffset !== null) {
            window.scrollBy({ top: card.getBoundingClientRect().top - anchorOffset, behavior: 'auto' });
          } else if (!anchorHandle && scrollY !== null) {
            window.scrollTo({ top: scrollY, behavior: 'auto' });
          }
        });
      });
    }

    toggleFilters(open) {
      if (open) this.toggleSort(false);
      this.elements.filters.classList.toggle('is-open', open);
      this.updateBodyLock();
    }

    toggleSort(open) {
      if (open) this.toggleFilters(false);
      this.elements.sortSheet.classList.toggle('is-open', open);
      this.elements.sortSheet.setAttribute('aria-hidden', String(!open));
      this.updateBodyLock();
    }

    updateBodyLock() {
      const overlayOpen = this.elements.filters.classList.contains('is-open') || this.elements.sortSheet.classList.contains('is-open');
      document.body.classList.toggle('rush-overlay-open', overlayOpen);
    }

    setLoading(loading, placement = 'replace') {
      if (placement === 'append') {
        if (loading) {
          this.elements.loadMore.hidden = false;
          this.elements.loadMore.style.display = 'flex';
          this.elements.loadMoreSpinner.hidden = false;
          this.elements.loadMoreText.textContent = 'Loading more styles...';
          this.elements.loadMoreText.hidden = false;
          this.elements.loadMoreRetry.hidden = true;
        } else {
          this.updatePagingControls();
        }
        return;
      }

      if (placement === 'prepend') {
        this.elements.loadPrevious.hidden = false;
        this.elements.loadPrevious.style.display = 'flex';
        this.elements.loadPreviousSpinner.hidden = !loading;
        this.elements.loadPreviousButton.hidden = loading;
        if (!loading) this.updatePagingControls();
        return;
      }

      this.elements.loading.hidden = !loading;
      this.elements.loading.style.display = loading ? 'flex' : '';
      if (loading) {
        this.elements.requestError.hidden = true;
        this.elements.empty.hidden = true;
        this.elements.grid.hidden = true;
        this.elements.loadMore.hidden = true;
      } else {
        this.elements.grid.hidden = false;
      }
    }

    showRequestError(placement = 'replace', page = this.state.page) {
      this.failedLoad = { placement, page };
      if (placement === 'append') {
        this.hasMore = true;
        this.elements.loadMore.hidden = false;
        this.elements.loadMore.style.display = 'flex';
        this.elements.loadMoreSpinner.hidden = true;
        this.elements.loadMoreText.textContent = 'We could not load more styles.';
        this.elements.loadMoreText.hidden = false;
        this.elements.loadMoreRetry.hidden = false;
        return;
      }

      if (placement === 'prepend') {
        this.hasPrevious = true;
        this.elements.loadPrevious.hidden = false;
        this.elements.loadPrevious.style.display = 'flex';
        this.elements.loadPreviousSpinner.hidden = true;
        this.elements.loadPreviousButton.hidden = false;
        this.elements.loadPreviousButton.textContent = 'Retry previous styles';
        return;
      }

      this.elements.grid.replaceChildren();
      this.elements.loading.hidden = true;
      this.elements.loading.style.display = '';
      this.elements.requestError.hidden = false;
      this.elements.requestError.style.display = 'flex';
      this.elements.loadMore.hidden = true;
    }

    formatMoney(value) {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
      }).format(Number(value) || 0);
    }

    formatNumber(value) {
      return new Intl.NumberFormat('en-IN').format(Number(value) || 0);
    }
  }

  if (!customElements.get('rush-collection')) {
    customElements.define('rush-collection', RushCollection);
  }
})();
